/**
 * D9: Data Isolation Indicator
 *
 * Customer-facing residency statement. Copy is intentionally aligned with the
 * authoritative infrastructure facts in `docs/runbooks/gdpr-data-residency.md`
 * and `docs/architecture/SECURITY-ARCHITECTURE.md`:
 *   - Tier 1 + Tier 2 tenants run on Hetzner self-hosted Postgres 18
 *     (ease-a1 primary + ease-a2 streaming replica, Nuremberg DE).
 *   - Cloudflare Workers reach the database via Hyperdrive + a Cloudflare
 *     Tunnel terminating at PgBouncer in Nuremberg. PII is not persisted at
 *     the edge.
 *
 * Previously this page advertised "EU (Frankfurt, aws-eu-central-1)" and
 * "dedicated Neon Serverless Postgres", which described neither the active
 * Tier 1 nor Tier 2 infrastructure and was a misleading compliance claim
 * shown to paying customers (SEC-C audit, PR #763).
 */
export default function DataIsolationPage() {
  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">Data Isolation</h1>

      <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-success)]/10">
            <span className="text-lg" aria-hidden="true">
              {"\u{1F512}"}
            </span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-success)]">Dedicated Database</h2>
            <p className="mt-1 text-sm text-[var(--color-success)]">
              Your data is stored in a dedicated database instance. No other customer can access your data.
            </p>

            <div className="mt-4 grid gap-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[var(--color-success)]/50" />
                <span className="text-[var(--color-success)]">Tenant isolation: Dedicated database per tenant</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[var(--color-success)]/50" />
                <span className="text-[var(--color-success)]">
                  Region: EU (Hetzner self-hosted, Nuremberg DE)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[var(--color-success)]/50" />
                <span className="text-[var(--color-success)]">Encryption: AES-256-GCM at rest, TLS 1.3 in transit</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[var(--color-success)]/50" />
                <span className="text-[var(--color-success)]">
                  High availability: Primary plus streaming replica with continuous WAL archiving and daily snapshots
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[var(--color-success)]/50" />
                <span className="text-[var(--color-success)]">GDPR: Full data portability and right-to-erasure supported</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        <p>
          <strong>Technical details:</strong> Tenant databases run on Hetzner self-hosted Postgres 18 in
          Nuremberg, Germany (EU), with a primary node and a streaming replica fronted by PgBouncer.
          Cloudflare Workers reach Postgres via Cloudflare Hyperdrive over a Cloudflare Tunnel; no
          customer PII is persisted at the edge. Each tenant has its own database (Tier 1) or its own
          schema with application-enforced isolation (Tier 2) &mdash; there is no shared multi-tenant
          table to leak between. All PII fields are encrypted with per-tenant keys (AES-256-GCM); GDPR
          erasure uses crypto-shredding, destroying the encryption key so PII becomes irrecoverable.
        </p>
      </div>
    </div>
  );
}
